import { useState } from "react";
import type { InventoryItem } from "../inventaire/inventaireTypes";
import { AlertCircle, ChefHat, ChevronDown, ChevronUp } from "lucide-react";

interface RecipeToConfigureListProps {
  recipes: InventoryItem[];
  onConfigure: (recipe: InventoryItem) => void;
}

const PREVIEW_COUNT = 9;

export function RecipeToConfigureList({
  recipes,
  onConfigure,
}: RecipeToConfigureListProps) {
  const [expanded, setExpanded] = useState(false);
  const visibleRecipes = expanded ? recipes : recipes.slice(0, PREVIEW_COUNT);
  const hasMore = recipes.length > PREVIEW_COUNT;

  if (recipes.length === 0) {
    return (
      <div className="bg-[var(--card)] rounded-lg border border-[var(--border)] p-12 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--color-olive)]/10 mb-4">
          <ChefHat className="w-8 h-8 text-[var(--color-olive)]" />
        </div>
        <h3 className="text-lg font-semibold text-[var(--foreground)] mb-2">
          Tous les articles ont une recette !
        </h3>
        <p className="text-sm text-[var(--muted-foreground)] max-w-md mx-auto">
          Tous vos articles du menu sont associés à une recette avec leurs ingrédients.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-[var(--color-amber)]" />
          <h3 className="font-semibold text-[var(--foreground)]">
            Articles sans recette ({recipes.length})
          </h3>
          {hasMore && !expanded && (
            <span className="text-xs text-[var(--muted-foreground)]">
              · aperçu {visibleRecipes.length} sur {recipes.length}
            </span>
          )}
        </div>
        <span className="text-xs text-[var(--muted-foreground)] bg-[var(--muted)] px-3 py-1 rounded-full">
          Priorité haute
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {visibleRecipes.map((recipe) => (
          <button
            key={recipe.id}
            onClick={() => onConfigure(recipe)}
            className="bg-[var(--card)] border border-[var(--border)] rounded-lg p-4 hover:border-[var(--color-steel)] hover:bg-[var(--muted)] transition-all text-left group"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-[var(--foreground)] truncate group-hover:text-[var(--color-steel)] transition-colors">
                  {recipe.nom}
                </h4>
                <p className="text-xs text-[var(--muted-foreground)] mt-0.5">
                  {recipe.categorie}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center pt-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md border border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--color-amber)] hover:text-[var(--color-amber)] hover:bg-[var(--muted)] transition-colors"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Réduire
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Voir tout ({recipes.length})
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}